'use client';

import { fireHermesWarmup } from '@/lib/hermes-warmup';
import { readSseStream } from '@/lib/sse';
import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';

type ChatMessage = {
  role: 'user' | 'assistant';
  content: string;
  streaming?: boolean;
  /**
   * 2026-06-17 — wall-clock when this assistant slot was created.
   * Drives the ThinkingIndicator's elapsed-second counter and the
   * 30 s watchdog → 「响应超时，重试」 surface (Amy QA P1).
   */
  startedAt?: number;
  timedOut?: boolean;
};

/**
 * Anonymous public-facing chat for /u/[username]. Each POST creates a brand-
 * new conversation upstream — no client-side history concept in V2.1.
 * SSE stream pattern mirrors the authenticated ChatShell so the typing UX
 * stays consistent.
 */
export function PublicChat({
  username,
  displayName,
}: {
  username: string;
  displayName: string;
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // 2026-06-17 — fire warmup on mount so the Container DO wakes up while
  // the visitor reads the profile header. Fail-soft; perf-only.
  // AXQA-20260618-04 (2026-06-18) — fireHermesWarmup dedupes within 45s
  // so re-mount (e.g. SPA navigation back to this profile) doesn't burn
  // a second ping on a container that's already warm.
  useEffect(() => {
    void fireHermesWarmup('/api/health/warmup');
  }, []);

  // AXQA-20260618-04 — re-fire warmup when the visitor focuses the
  // input box. Catches the case where they sat reading the profile for
  // > 60s (CF Containers' typical scale-down window): the mount-time
  // warmup expired, the container went back to sleep, but our 45s
  // helper TTL skips automatically if the page-mount ping is still
  // fresh — so this listener is cheap when not needed.
  const onInputFocus = useCallback(() => {
    void fireHermesWarmup('/api/health/warmup');
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on every messages mutation including streaming deltas
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const send = useCallback(
    async (text: string) => {
      setLoading(true);
      setError(null);
      const userMsg: ChatMessage = { role: 'user', content: text };
      const assistantMsg: ChatMessage = {
        role: 'assistant',
        content: '',
        streaming: true,
        startedAt: Date.now(),
      };
      setMessages((prev) => [...prev, userMsg, assistantMsg]);

      try {
        const res = await fetch(`/api/public/u/${encodeURIComponent(username)}/chat`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ content: text }),
        });
        if (!res.ok || !res.body) {
          // 2026-06-19 — owner daily cap exceeded → friendly toast.
          if (res.status === 429) {
            const txt = await res.text().catch(() => '');
            try {
              const j = JSON.parse(txt) as { hint?: string };
              throw new Error(j.hint || '当前分身今日已达用量上限，请稍后再试');
            } catch {
              throw new Error('当前分身今日已达用量上限，请稍后再试');
            }
          }
          throw new Error(`stream ${res.status}`);
        }

        await readSseStream(res.body, (ev) => {
          if (ev.type === 'chat_meta') {
            // AXQA-20260618-20/21 — emit SOUL version + Hermes build to
            // the browser console so QA can confirm public chat picks
            // up the latest persona save without DevTools Network panel.
            // 2026-06-19 — flat stringified line + structured event so
            // headless DOM scrapers can read hermes_build without
            // object expansion.
            if (typeof console !== 'undefined') {
              const m = ev as unknown as {
                soul_version?: number | null;
                tone_preset?: string | null;
                when_unsure?: string | null;
                hermes_build?: string | null;
                scope?: string | null;
              };
              console.warn(
                `[chat-meta] soul_version=${m.soul_version ?? 'null'} tone_preset=${m.tone_preset ?? 'null'} when_unsure=${m.when_unsure ?? 'null'} hermes_build=${m.hermes_build ?? 'null'} scope=${m.scope ?? 'null'}`,
              );
              console.warn('[chat-meta]', ev);
            }
          } else if (ev.type === 'delta' && typeof ev.text === 'string') {
            const chunk = ev.text;
            setMessages((prev) => {
              const next = prev.slice();
              const last = next[next.length - 1];
              if (last && last.role === 'assistant') {
                next[next.length - 1] = { ...last, content: last.content + chunk };
              }
              return next;
            });
          } else if (ev.type === 'done') {
            setMessages((prev) => {
              const next = prev.slice();
              const last = next[next.length - 1];
              if (last && last.role === 'assistant') {
                next[next.length - 1] = { ...last, streaming: false };
              }
              return next;
            });
          }
        });

        setMessages((prev) => {
          const next = prev.slice();
          const last = next[next.length - 1];
          if (last && last.role === 'assistant' && last.streaming) {
            next[next.length - 1] = { ...last, streaming: false };
          }
          return next;
        });
        setInput('');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Streaming failed.');
        setMessages((prev) => {
          const next = prev.slice();
          const last = next[next.length - 1];
          if (last && last.role === 'assistant') {
            next[next.length - 1] = {
              ...last,
              streaming: false,
              content: last.content || '⚠ Failed to load response.',
            };
          }
          return next;
        });
      } finally {
        setLoading(false);
      }
    },
    [username],
  );

  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const text = input.trim();
      if (!text || loading) return;
      send(text);
    },
    [input, loading, send],
  );

  return (
    <div
      // V2.11 (2026-05-21) — same height-fit logic as TwinChatLp.
      className="grid grid-rows-[1fr_auto] rounded-2xl border border-soft bg-white h-[calc(100dvh-260px-env(safe-area-inset-bottom))] md:h-[calc(100dvh-200px)] min-h-[360px]"
    >
      <div ref={scrollRef} className="px-5 py-5 overflow-y-auto text-base">
        {messages.length === 0 && (
          <p className="text-dusk text-sm m-0">
            Ask {displayName} anything. Responses carry confidence tags (
            <code className="font-mono text-xs">实时</code> /{' '}
            <code className="font-mono text-xs">历史</code> /{' '}
            <code className="font-mono text-xs">基于推断</code>) — this is an AI representation, not
            financial advice.
          </p>
        )}
        <div className="mt-4 flex flex-col gap-3">
          {messages.map((m, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: append-only
              key={i}
              className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] px-4 py-3 leading-relaxed whitespace-pre-wrap break-words rounded-2xl ${
                  m.role === 'user'
                    ? 'bg-warmth-dark text-white rounded-br-sm'
                    : 'bg-cream border border-soft text-ink rounded-bl-sm'
                }`}
              >
                {/*
                  2026-06-17 — pre-first-token "正在思考…(N 秒)" indicator,
                  parity with TwinChatLp's ChatShell. Amy QA P1: the prior
                  `…` glyph gave no signal that work was happening, leading
                  visitors to mistake a 12-30 s cold start for a hang.
                */}
                {m.streaming && m.content === '' ? (
                  <ThinkingIndicator startedAt={m.startedAt ?? Date.now()} />
                ) : (
                  <>
                    {m.content}
                    {m.streaming && (
                      <span aria-label="正在输入" className="text-dusk ml-1">
                        …
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={onSubmit} className="border-t border-soft px-4 py-3 bg-white rounded-b-2xl">
        {error && (
          <p role="alert" className="text-warmth text-sm m-0 mb-2">
            {error}
          </p>
        )}
        <div className="flex items-end gap-2">
          <label htmlFor="public-chat-input" className="sr-only">
            向 {displayName} 提问
          </label>
          <textarea
            id="public-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onFocus={onInputFocus}
            placeholder={`问 ${displayName}…`}
            disabled={loading}
            rows={1}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                const text = input.trim();
                if (text && !loading) send(text);
              }
            }}
            className="flex-1 px-4 py-2 rounded-full border border-soft bg-cream/40 text-base text-ink placeholder:text-dusk/60 focus:outline-none focus:ring-2 focus:ring-warmth/40 focus:border-warmth/40 transition resize-none disabled:opacity-60"
            style={{ minHeight: 36, maxHeight: 120, lineHeight: '1.4' }}
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="shrink-0 px-4 min-h-[36px] rounded-full bg-warmth-dark text-white font-medium text-sm whitespace-nowrap hover:bg-warmth transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {loading ? '…' : '发送'}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * 2026-06-17 — pre-first-token wait state. Counts up from startedAt,
 * mirrors TwinChatLp's authenticated variant. After 30 s, switches to a
 * "响应较慢，可重新发送" copy so users know it isn't silently hung —
 * the Streaming retry itself is one click away (resend the last text).
 */
function ThinkingIndicator({ startedAt }: { startedAt: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const elapsedSec = Math.max(0, Math.floor((now - startedAt) / 1000));
  const slow = elapsedSec >= 30;
  return (
    <span className="inline-flex items-center gap-2 text-dusk text-[15px] italic">
      <span aria-hidden className="inline-flex gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-dusk/60 animate-bounce [animation-delay:-0.3s]" />
        <span className="w-1.5 h-1.5 rounded-full bg-dusk/60 animate-bounce [animation-delay:-0.15s]" />
        <span className="w-1.5 h-1.5 rounded-full bg-dusk/60 animate-bounce" />
      </span>
      <span>{slow ? '响应较慢，可稍后重试…' : '正在思考…'}</span>
      <span className="text-text-mute text-[13px] not-italic tabular-nums">({elapsedSec} 秒)</span>
    </span>
  );
}
